import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ResetPassword } from "@/domain/usecase/ResetPassword";
import {
  initialResetPasswordFormValues,
  resetPasswordSchema,
  type ResetPasswordFormValues,
} from "./passwordResetSchema";
import {
  isAbortError,
  toFieldErrors,
  toUserMessage,
  type FieldErrors,
} from "./PasswordResetShared.ts";

type SubmitState =
  | { type: "idle" }
  | { type: "submitting" }
  | { type: "changed" }
  | { type: "failed"; message: string };

type Args = {
  token: string;
  resetPassword: ResetPassword;
};

export function useResetPasswordViewModel({ token, resetPassword }: Args) {
  const [values, setValues] = useState<ResetPasswordFormValues>(
    initialResetPasswordFormValues,
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>({
    type: "idle",
  });

  const submitAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      submitAbortControllerRef.current?.abort();
    };
  }, []);

  const hasToken = token.trim().length > 0;
  const isBusy = submitState.type === "submitting";
  const canSubmit = hasToken && !isBusy;

  const setField = useCallback(
    <K extends keyof ResetPasswordFormValues>(
      field: K,
      value: ResetPasswordFormValues[K],
    ) => {
      setValues((current) => ({
        ...current,
        [field]: value,
      }));

      setFieldErrors((current) => {
        const next = { ...current };
        delete next[String(field)];
        return next;
      });

      if (submitState.type === "failed") {
        setSubmitState({ type: "idle" });
      }
    },
    [submitState.type],
  );

  const submit = useCallback(async () => {
    if (!hasToken) return;

    setSubmitState({ type: "idle" });
    setFieldErrors({});

    const parsed = resetPasswordSchema.safeParse(values);

    if (!parsed.success) {
      setFieldErrors(toFieldErrors(parsed.error));
      return;
    }

    submitAbortControllerRef.current?.abort();

    const controller = new AbortController();
    submitAbortControllerRef.current = controller;

    setSubmitState({ type: "submitting" });

    try {
      await resetPassword.execute(
        {
          token,
          password: parsed.data.password,
        },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      setSubmitState({ type: "changed" });
    } catch (error) {
      if (isAbortError(error)) return;

      setSubmitState({
        type: "failed",
        message: toUserMessage(
          error,
          "Das Passwort konnte nicht geändert werden. Der Link ist möglicherweise ungültig oder abgelaufen.",
        ),
      });
    } finally {
      if (submitAbortControllerRef.current === controller) {
        submitAbortControllerRef.current = null;
      }
    }
  }, [hasToken, resetPassword, token, values]);

  return useMemo(
    () => ({
      values,
      fieldErrors,
      submitState,
      hasToken,
      isBusy,
      canSubmit,
      actions: {
        setField,
        submit,
      },
    }),
    [
      values,
      fieldErrors,
      submitState,
      hasToken,
      isBusy,
      canSubmit,
      setField,
      submit,
    ],
  );
}
